import type { ShopperProducts, ShopperSearch } from '@/scapi';

/**
 * One B2B segment from the customer profile (`Profile.custom.b2bSegments`).
 */
export interface CustomerSegment {
    /** Client code, e.g. "Z2". Informational only, not used for access. */
    code: string | null;
    /** Product type as stored on the profile, e.g. "Action Sports". */
    productType: string;
    /** Tier as stored on the profile, e.g. "Better". */
    tier: string;
}

/**
 * What the storefront knows about the current shopper's ordering rights.
 */
export interface ShopperSegment {
    /** False when the segment could not be resolved; the UI must not offer Add to Cart. */
    available: boolean;
    /** False when segmentation is switched off in BM — everything is orderable. */
    segmentationActive: boolean;
    /** True when the shopper is in the BM admin customer group and may order any product. */
    isAdminAccess: boolean;
    /** Segments as stored on the profile; normalized only when compared. */
    segments: CustomerSegment[];
}

/** Fail-closed shopper state when the segment could not be resolved; hide Add to Cart. */
export const UNAVAILABLE_SEGMENT: ShopperSegment = Object.freeze({
    available: false,
    segmentationActive: true,
    isAdminAccess: false,
    segments: [],
}) as ShopperSegment;

/** Default outside `SegmentationProvider` (Storybook, isolated tests): no restrictions. */
export const NO_GATING_SEGMENT: ShopperSegment = Object.freeze({
    available: true,
    segmentationActive: false,
    isAdminAccess: false,
    segments: [],
}) as ShopperSegment;

/** Product types and tiers parsed from `c_fishDownForDot`, already normalized. */
export interface ProductSegments {
    types: string[];
    tiers: string[];
}

const SEGMENT_SEPARATOR = '|';
const VALUE_SEPARATOR = '/';
const ANNOTATION_PATTERN = /\([^)]*\)/g;
const FISH_DOWN_PROPERTY_IDS = new Set(['c_fishDownForDot', 'fishDownForDot']);

/**
 * Lower-case a product type or tier for comparison. Annotations such as "(Fish Down)" do not affect access,
 * so they are dropped. Must match `normalizeSegmentValue` in the SFCC `segmentationHelpers.js`.
 */
export function normalizeSegmentValue(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).replace(ANNOTATION_PATTERN, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Split a "/"-separated list into unique normalized non-empty values. */
const splitSegmentValues = (value: string): string[] =>
    Array.from(new Set(value.split(VALUE_SEPARATOR).map(normalizeSegmentValue).filter(Boolean)));

/**
 * Parse `c_fishDownForDot` ("<type> / <type> | <tier> / <tier> (Fish Down)").
 * Returns null when the value is not a string, has no "|", or either side is empty.
 */
export function parseProductSegments(fishDownForDot: unknown): ProductSegments | null {
    if (typeof fishDownForDot !== 'string') {
        return null;
    }

    const separatorIndex = fishDownForDot.indexOf(SEGMENT_SEPARATOR);
    if (separatorIndex === -1) {
        return null;
    }

    const types = splitSegmentValues(fishDownForDot.slice(0, separatorIndex));
    const tiers = splitSegmentValues(fishDownForDot.slice(separatorIndex + 1));
    if (types.length === 0 || tiers.length === 0) {
        return null;
    }

    return { types, tiers };
}

/**
 * Validate a list of customer segments (shopper-segment response, cookie, hook rejection detail).
 * Returns null when the value is not an array or any entry is malformed; blank entries are dropped.
 */
export function parseCustomerSegments(value: unknown): CustomerSegment[] | null {
    if (!Array.isArray(value)) {
        return null;
    }

    const segments: CustomerSegment[] = [];
    for (const entry of value) {
        if (typeof entry !== 'object' || entry === null) {
            return null;
        }

        const { code, productType, tier } = entry as Record<string, unknown>;
        if (
            typeof productType !== 'string' ||
            typeof tier !== 'string' ||
            (code !== null && code !== undefined && typeof code !== 'string')
        ) {
            return null;
        }

        if (normalizeSegmentValue(productType) && normalizeSegmentValue(tier)) {
            segments.push({ code: code?.trim() || null, productType: productType.trim(), tier: tier.trim() });
        }
    }

    return segments;
}

/** Read c_fishDownForDot from a search hit: representedProduct, then customProperties, then top-level. */
function getSearchHitFishDownForDot(hit: Record<string, unknown>): unknown {
    const represented = hit.representedProduct as Record<string, unknown> | undefined;
    if (typeof represented?.c_fishDownForDot === 'string' && represented.c_fishDownForDot.trim()) {
        return represented.c_fishDownForDot;
    }

    const customProperties = hit.customProperties as Array<{ id?: string; value?: unknown }> | undefined;
    const property = customProperties?.find((entry) => entry.id !== undefined && FISH_DOWN_PROPERTY_IDS.has(entry.id));
    if (typeof property?.value === 'string' && property.value.trim()) {
        return property.value;
    }

    return hit.c_fishDownForDot;
}

/** Read c_fishDownForDot for a Product, search hit, or basket line (`itemId`). */
function getItemFishDownForDot(item: unknown): unknown {
    if (!item || typeof item !== 'object') {
        return undefined;
    }

    const record = item as Record<string, unknown>;
    if (typeof record.productId === 'string' && typeof record.itemId !== 'string') {
        return getSearchHitFishDownForDot(record);
    }

    return record.c_fishDownForDot;
}

/**
 * True when this shopper segment allows ordering the item: at least one customer segment must match
 * the product by both type and tier within the same entry.
 */
export function isAllowedBySegment(
    segment: ShopperSegment,
    item?: ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'] | null
): boolean {
    if (!segment.available) {
        return false;
    }
    if (!segment.segmentationActive || segment.isAdminAccess) {
        return true;
    }
    if (segment.segments.length === 0) {
        return false;
    }

    const productSegments = parseProductSegments(getItemFishDownForDot(item));
    if (!productSegments) {
        return false;
    }

    return segment.segments.some(
        (customerSegment) =>
            productSegments.types.includes(normalizeSegmentValue(customerSegment.productType)) &&
            productSegments.tiers.includes(normalizeSegmentValue(customerSegment.tier))
    );
}
